<template>
  <div class="overflow-x-auto" :class="{ [$style.fill]: fill }">
    <div :class="$style.items">
      <div
        v-for="tab in options"
        :key="tab.key || tab.value"
        :class="{
          [$style.selected]: modelValue === tab.value,
          [$style.navItem]: true,
        }"
        :data-testid="tab.dataTestid || `tabnav-${tab.key || tab.value}`"
        role="button"
        @click="
          () => {
            $track('tabnav-click', tab);
            $emit('update:modelValue', tab.value);
            $emit('tabChange', tab.value);
          }
        "
      >
        <div
          :class="{
            [$style.navItemLabel]: true,
          }"
        >
          <div class="flex items-center gap-2">
            <Icon v-if="tab.icon" v-bind="getIconProps(tab.icon)" />
            <span>{{ tab.label }}</span>
            <span v-if="tab.notify" :class="$style.notification" />
            <Badge
              v-if="tab.quoteStatus && !store.isMaker"
              class="text-xs leading-none"
              v-bind="
                getStatusBadge({
                  status: tab.quoteStatus,
                  userType: store.activeSessionType,
                })
              "
              pill
            />
          </div>
        </div>
        <div v-if="tab.description" :class="$style.description">
          {{ tab.description }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getStatusBadge } from '@/helpers/quote-helpers';
import { useActiveUserStore } from '@/stores/user';

defineProps({
  options: {
    type: Array,
    required: true,
  },
  fill: {
    type: Boolean,
  },
  modelValue: {
    type: String,
  },
});

defineEmits(['update:modelValue', 'tabChange']);

const getIconProps = (icon) => {
  if (typeof icon === 'string') {
    return { icon };
  }

  return icon;
};

const store = useActiveUserStore();
</script>

<style module>
.navItem {
  --colorHighlighted: var(--colorTabHighlighted, var(--amber-600));

  cursor: pointer;
  font-size: 1rem;
  border-bottom: 2px solid transparent;
  transition: border-bottom 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--navBarPadding, 0.5rem 0);
  font-weight: 400;

  &:hover {
    color: var(--colorHighlighted);
  }

  &.selected {
    color: var(--colorHighlighted);
    border-bottom-color: var(--colorHighlighted);
    font-weight: 500;
  }

  /* compensate for bold thickness shifting the element by making it slightly bigger */
  &:not(.selected) {
    padding-right: 2px;
  }
}

.items {
  display: flex;
  gap: 2rem;
}

.navItemLabel {
  text-decoration: none;
  white-space: nowrap;
  margin-bottom: var(--space-s);

  .icon {
    font-size: 18px;
    padding-right: 0.2em;

    &.replaced,
    &.missed,
    &.expired,
    &.declined,
    &.feedback {
      display: none;
    }
  }
}

.description {
  font-weight: 400;
  color: var(--neutral-600);
  margin-top: 0.5rem;
}

.notification {
  --size: 0.7em;

  display: inline-block;
  width: var(--size);
  height: var(--size);
  border-radius: var(--size);
  background-color: var(--amber-600);
  vertical-align: super;
  margin-left: 0.2em;
}

.fill {
  width: 100%;
  height: 100%;
  color: var(--neutral-800);

  .navItem:not(.selected) {
    padding-right: 0;
  }

  .items {
    height: 100%;
    align-items: center;
    gap: 0;

    & > div {
      flex: 0 1 4.5rem;
      height: 100%;
      justify-content: center;
      align-content: center;
    }

    [data-i] {
      --fontSize: 1.5rem;
    }
  }
}
</style>
